import moment from 'moment-timezone';
import {i18n} from 'src/i18n';
import {findMultipleChoiceTreeFullPath, getMultipleChoiceTreeDisplayLabel} from 'src/modules/shared/alternativeTreeDisplay';
import {isPlastyvergTenant} from 'src/modules/tarea/dynamicSubsectionPdfGrouping';
import {BLUMAR_CALIDAD_TENANT_ID} from 'src/security/taskPdfFeatureGates';

export type ParcialColumn = {
  label: string;
  questionType: string;
  /** Nombre de la subsección a la que pertenece la pregunta (si aplica). */
  subsectionLabel?: string;
  /** Índice entre las preguntas del procedimiento (matching estable entre filas). */
  questionIndex?: number;
};

export type SubsectionHeaderSpan = {
  label: string;
  colSpan: number;
};

/** Umbral de columnas para forzar hoja grande / cabeceras de subsección (tenant Blumar Calidad). */
export const CHILD_TASKS_WIDE_TABLE_COLUMN_THRESHOLD = 30;

export const isChildTasksWideTableMode = (tenantId: string | undefined | null, columnCount: number) =>
  String(tenantId || '') === BLUMAR_CALIDAD_TENANT_ID && columnCount > CHILD_TASKS_WIDE_TABLE_COLUMN_THRESHOLD;

const normalizeProcedureQuestionLabel = (label: any) =>
  String(label ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[°º]/g, '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();

const POZOS_QUESTION_LABELS = new Set(['pozo', 'pozos', 'numero de pozos', 'n de pozos']);

export const isBlumarPozosQuestionLabel = (label: any, tenantId?: string | null) =>
  String(tenantId || '') === BLUMAR_CALIDAD_TENANT_ID && POZOS_QUESTION_LABELS.has(normalizeProcedureQuestionLabel(label));

const CAL_CN_INICIO_LABEL = 'CAL CN INICIO';

const isCalCnQuestionLabel = (label: any) => {
  const normalized = normalizeProcedureQuestionLabel(label);
  return normalized === 'cal cn' || normalized === 'cal cn inicio';
};

const procedureQuestionLabelsMatch = (left: any, right: any) => {
  if (String(left || '') === String(right || '')) return true;
  return isCalCnQuestionLabel(left) && isCalCnQuestionLabel(right);
};

const collapseCalCnParcialColumns = (columns: ParcialColumn[]): ParcialColumn[] => {
  const result: ParcialColumn[] = [];
  const calCnIndexByKey = new Map<string, number>();
  const calCnIsInicioByKey = new Map<string, boolean>();
  for (const column of columns) {
    if (!isCalCnQuestionLabel(column.label)) {
      result.push(column);
      continue;
    }
    const key = `${column.subsectionLabel || ''}|cal-cn`;
    const existingIndex = calCnIndexByKey.get(key);
    const nextIsInicio = normalizeProcedureQuestionLabel(column.label) === 'cal cn inicio';
    const next = {...column, label: CAL_CN_INICIO_LABEL};
    if (existingIndex == null) {
      calCnIndexByKey.set(key, result.length);
      calCnIsInicioByKey.set(key, nextIsInicio);
      result.push(next);
      continue;
    }
    if (!calCnIsInicioByKey.get(key) && nextIsInicio) {
      result[existingIndex] = next;
      calCnIsInicioByKey.set(key, true);
    }
  }
  return result;
};

/** Permite extender a todos los tenants los ajustes de formato PDF solicitados para Plastyverg. */
export const APPLY_PARTIAL_PDF_FORMAT_CHANGES_TO_ALL_TENANTS = false;

export const shouldApplyPartialPdfFormatChanges = (tenantId?: string | null) =>
  APPLY_PARTIAL_PDF_FORMAT_CHANGES_TO_ALL_TENANTS || isPlastyvergTenant(tenantId);

export const getExecutorSignatureColumnLabel = (tenantId?: string | null) =>
  i18n(
    isPlastyvergTenant(tenantId)
      ? 'entities.tarea.executorSignatureInspector'
      : 'entities.tarea.executorSignature',
  );

const buildPerfilEmpleadoDisplayName = (perfilEmpleado: any) => {
  if (!perfilEmpleado || typeof perfilEmpleado !== 'object') {
    return '';
  }
  if (perfilEmpleado.label != null && String(perfilEmpleado.label).trim() !== '') {
    return String(perfilEmpleado.label).trim();
  }
  if (perfilEmpleado.fullName != null && String(perfilEmpleado.fullName).trim() !== '') {
    return String(perfilEmpleado.fullName).trim();
  }
  const nombre = perfilEmpleado.nombre != null ? String(perfilEmpleado.nombre).trim() : '';
  const apellidos = perfilEmpleado.apellidos != null ? String(perfilEmpleado.apellidos).trim() : '';
  const composed = [nombre, apellidos].filter(Boolean).join(' ').trim();
  if (composed) {
    return composed;
  }
  if (perfilEmpleado.descripcion != null && String(perfilEmpleado.descripcion).trim() !== '') {
    return String(perfilEmpleado.descripcion).trim();
  }
  if (perfilEmpleado.email != null && String(perfilEmpleado.email).trim() !== '') {
    return String(perfilEmpleado.email).trim();
  }
  return '';
};

/** Nombre visible bajo una firma: perfilEmpleado primero, luego datos del usuario. */
export const getSignaturePersonDisplayName = (person: any) => {
  if (!person || typeof person !== 'object') {
    return '';
  }
  const fromPerfilEmpleado = buildPerfilEmpleadoDisplayName(person.perfilEmpleado);
  if (fromPerfilEmpleado) {
    return fromPerfilEmpleado;
  }
  if (person.fullName != null && String(person.fullName).trim() !== '') {
    return String(person.fullName).trim();
  }
  if (person.label != null && String(person.label).trim() !== '') {
    return String(person.label).trim();
  }
  if (person.email != null && String(person.email).trim() !== '') {
    return String(person.email).trim();
  }
  return '';
};

export const getRecordId = (value: any) => {
  const id = value?.id ?? value?._id ?? value;
  return id == null ? '' : String(id);
};

export const getTaskDisplayNumber = (task: any, fallback?: number) => task?.displayNumber || task?.numeroTarea || fallback || '';

/** @deprecated Prefer getTaskVisualLabel from taskVisualLabel.ts for UI codes. */
export {getTaskVisualLabel} from 'src/modules/tarea/taskVisualLabel';

/**
 * Si true, la columna N° de tablas de subtareas usa displayNumber/numeroTarea
 * (y en PDF solo numera i+1 cuando hay un único grupo). Si false (default),
 * siempre muestra el índice de fila (i+1).
 * Plastyverg siempre usa i+1, independientemente de este flag.
 */
export const USE_CHILD_TABLE_TASK_DISPLAY_NUMBER = false;

/** Número visible en la columna N° de tablas de subtareas (UI y PDF). */
export const getChildTableRowLabel = (task: any, index: number, tenantId?: string | null) => {
  if (isPlastyvergTenant(tenantId) || !USE_CHILD_TABLE_TASK_DISPLAY_NUMBER) {
    return index + 1;
  }
  return getTaskDisplayNumber(task, index + 1);
};

/**
 * En PDF: con el flag legacy, i+1 solo si hay un único grupo; si no, displayNumber.
 * Con el default actual (y siempre en Plastyverg), siempre i+1.
 */
export const shouldUseSequentialChildTableRowNumbers = (groupCount: number, tenantId?: string | null) => {
  if (isPlastyvergTenant(tenantId) || !USE_CHILD_TABLE_TASK_DISPLAY_NUMBER) {
    return true;
  }
  return groupCount === 1;
};

const TIME_ONLY_PARSE_FORMATS = ['HH:mm:ss', 'H:mm:ss', 'HH:mm', 'H:mm'];

/** Minutos desde medianoche; null si no se puede parsear. */
const parseTimeOfDayMinutes = (value: any): number | null => {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) {
      const parsed = moment(trimmed, TIME_ONLY_PARSE_FORMATS, true);
      if (parsed.isValid()) return parsed.hours() * 60 + parsed.minutes();
    }
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed.hours() * 60 + parsed.minutes() : null;
};

/** Respuesta cruda de una pregunta del procedimiento por label exacto (p. ej. FECHA / HORA). */
const getProcedureAnswerRawByLabel = (task: any, label: string) => {
  const item = (task?.listaProcedimiento || []).find((entry) => {
    if (!(entry?.tipo === 'pregunta' || entry?.pregunta)) return false;
    const name = entry?.pregunta?.label || entry?.pregunta?.titulo || entry?.label || entry?.titulo || '';
    return String(name).trim().toUpperCase() === label;
  });
  if (!item?.tipoRespuesta) return null;
  const value = item[item.tipoRespuesta];
  return value == null || value === '' ? null : value;
};

/**
 * Orden de subtareas: fin → fechaProgramacion → FECHA(+HORA) del procedimiento →
 * solo HORA (anclada al día de createdAt si existe) → createdAt.
 */
export const getChildTaskSortTimestamp = (task: any) => {
  const procedureFecha = getProcedureAnswerRawByLabel(task, 'FECHA');
  const procedureHora = getProcedureAnswerRawByLabel(task, 'HORA');
  const fechaParsed = procedureFecha != null ? moment(procedureFecha) : null;
  const horaMinutes = parseTimeOfDayMinutes(procedureHora);

  if (fechaParsed?.isValid()) {
    if (horaMinutes != null) {
      return fechaParsed.clone().startOf('day').add(horaMinutes, 'minutes').valueOf();
    }
    return fechaParsed.valueOf();
  }

  if (horaMinutes != null) {
    const created = moment();
    const base = created.isValid() ? created.clone().startOf('day') : moment.utc(0);
    return base.add(horaMinutes, 'minutes').valueOf();
  }

  const createdAt = moment(task?.createdAt);
  if (createdAt.isValid()) return createdAt.valueOf();

  const primary = task?.fin || task?.fechaProgramacion;
  const primaryParsed = moment(primary);
  if (primaryParsed.isValid()) return primaryParsed.valueOf();
  return Number.MAX_SAFE_INTEGER;
};

export const sortChildTasksByEndDateAsc = (tasks: any[] = []) =>
  [...tasks].sort((left, right) => getChildTaskSortTimestamp(left) - getChildTaskSortTimestamp(right));

export const getParcialSortTimestamp = (parcial: any) => {
  const date = parcial?.fechaFirmada || parcial?.createdAt || parcial?.updatedAt;
  const parsed = moment(date);
  return parsed.isValid() ? parsed.valueOf() : 0;
};

export const sortParcialesByDateAsc = (parciales: any[] = []) =>
  [...parciales].sort((left, right) => getParcialSortTimestamp(left) - getParcialSortTimestamp(right));

export const getParcialDisplayDate = (parcial: any) => {
  const date = parcial?.fechaFirmada || parcial?.createdAt || parcial?.updatedAt;
  const parsed = moment(date);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : '-';
};

export const getParcialGroupTitle = (parcial: any) => {
  if (parcial?.estado === 'Pendiente') {
    return i18n('entities.firma.parcial.estadoTag.Pendiente');
  }

  const date = getParcialDisplayDate(parcial);
  const titleKeyByEstado: Record<string, string> = {
    Aprobada: 'entities.firma.parcial.groupTitleAprobada',
    Rechazada: 'entities.firma.parcial.groupTitleRechazada',
  };
  const key = titleKeyByEstado[parcial?.estado] || 'entities.firma.parcial.groupTitleDefault';
  return i18n(key, date);
};

export const buildParcialResultMap = (resultadosHijas: any[] = []) => {
  const resultMap = new Map<string, any>();
  resultadosHijas.forEach((resultado) => {
    const id = getRecordId(resultado?.tarea);
    if (id) {
      resultMap.set(id, resultado);
    }
  });
  return resultMap;
};

export const groupTasksByModelo = (tasks: any[] = []) => {
  const byModelo = new Map<string, {modelo: any; inspecciones: any[]}>();
  sortChildTasksByEndDateAsc(tasks).forEach((task) => {
    const modelo = task?.modeloTarea;
    const key = getRecordId(modelo) || 'sin-modelo';
    if (!byModelo.has(key)) {
      byModelo.set(key, {modelo, inspecciones: []});
    }
    byModelo.get(key)!.inspecciones.push(task);
  });
  return Array.from(byModelo.values());
};

export const buildParcialColumns = (inspecciones: any[] = []): ParcialColumn[] => {
  let columns: ParcialColumn[] = [];
  inspecciones.forEach((task) => {
    const extracted = extractProcedureQuestionsWithSubsection(task?.listaProcedimiento);
    if (extracted.length > columns.length) {
      columns = extracted.map(({item, subsectionLabel, questionIndex}) => ({
        label: item?.pregunta?.label || item?.pregunta?.titulo || i18n('entities.firma.parcial.blankQuestion'),
        questionType: item?.tipoRespuesta,
        subsectionLabel: subsectionLabel || undefined,
        questionIndex,
      }));
    }
  });
  return collapseCalCnParcialColumns(columns);
};

/** Agrupa columnas consecutivas con la misma subsección para cabeceras con colspan. */
export const buildSubsectionHeaderSpans = (columns: ParcialColumn[] = []): SubsectionHeaderSpan[] => {
  const spans: SubsectionHeaderSpan[] = [];
  columns.forEach((column) => {
    const label = column.subsectionLabel || '';
    const last = spans[spans.length - 1];
    if (last && last.label === label) {
      last.colSpan += 1;
      return;
    }
    spans.push({label, colSpan: 1});
  });
  return spans;
};

export const hasSubsectionHeaderSpans = (columns: ParcialColumn[] = []) =>
  columns.some((column) => Boolean(column.subsectionLabel));

/**
 * Recorre listaProcedimiento asociando cada pregunta a la subsección vigente.
 */
export type ExtractedProcedureQuestion = {
  item: any;
  subsectionLabel: string;
  questionIndex: number;
  ancestorNotApplicable: boolean;
};

export const extractProcedureQuestionsWithSubsection = (listaProcedimiento: any[] = []) => {
  const result: ExtractedProcedureQuestion[] = [];
  let currentSubsection = '';
  const tracker = createAncestorNaTracker();

  (listaProcedimiento || []).forEach((item) => {
    tracker.observe(item);
    if (item?.tipo === 'subseccion') {
      currentSubsection = String(item?.categoria || '').trim();
      return;
    }
    if (item?.tipo === 'seccion') {
      currentSubsection = '';
      return;
    }
    if (item?.tipo === 'pregunta' || item?.pregunta) {
      result.push({
        item,
        subsectionLabel: currentSubsection,
        questionIndex: result.length,
        ancestorNotApplicable: tracker.covered,
      });
    }
  });

  return result;
};

const procedureEntryLabel = (entry: ExtractedProcedureQuestion) =>
  entry?.item?.pregunta?.label || entry?.item?.pregunta?.titulo || '';

const procedureEntryHasAnswer = (entry?: ExtractedProcedureQuestion) => {
  const item = entry?.item;
  if (!item?.tipoRespuesta) return false;
  const value = item[item.tipoRespuesta];
  if (value == null || value === '') return false;
  if (Array.isArray(value)) {
    return value.some((option) => option?.value || option?.label || option?.fullName || option?.nombre);
  }
  if (typeof value === 'object') {
    return Boolean(value.label || value.value || value.id);
  }
  return true;
};

export const findProcedureQuestionEntry = (task: any, column: ParcialColumn) => {
  const extracted = extractProcedureQuestionsWithSubsection(task?.listaProcedimiento);

  const matchesColumnType = (entry: ExtractedProcedureQuestion) =>
    !column.questionType || entry.item?.tipoRespuesta === column.questionType;

  const matchByLabel = () => {
    const bySubsection = extracted.find((entry) => {
      const subsectionOk = !column.subsectionLabel || entry.subsectionLabel === column.subsectionLabel;
      return procedureQuestionLabelsMatch(procedureEntryLabel(entry), column.label) && matchesColumnType(entry) && subsectionOk;
    });
    if (bySubsection) return bySubsection;

    return extracted.find(
      (entry) => procedureQuestionLabelsMatch(procedureEntryLabel(entry), column.label) && matchesColumnType(entry),
    );
  };

  if (isCalCnQuestionLabel(column.label)) {
    const bySubsection = extracted.filter((entry) => {
      const subsectionOk = !column.subsectionLabel || entry.subsectionLabel === column.subsectionLabel;
      return isCalCnQuestionLabel(procedureEntryLabel(entry)) && matchesColumnType(entry) && subsectionOk;
    });
    const matches = bySubsection.length
      ? bySubsection
      : extracted.filter((entry) => isCalCnQuestionLabel(procedureEntryLabel(entry)) && matchesColumnType(entry));
    const inicio = matches.find((entry) => normalizeProcedureQuestionLabel(procedureEntryLabel(entry)) === 'cal cn inicio');
    const legacy = matches.find((entry) => normalizeProcedureQuestionLabel(procedureEntryLabel(entry)) === 'cal cn');
    if (procedureEntryHasAnswer(inicio)) return inicio;
    if (procedureEntryHasAnswer(legacy)) return legacy;
    return inicio || legacy || (column.questionIndex != null ? extracted[column.questionIndex] : undefined);
  }

  if (column.questionIndex != null && extracted[column.questionIndex]) {
    return extracted[column.questionIndex];
  }

  return matchByLabel();
};

export const findProcedureQuestion = (task: any, column: ParcialColumn) => findProcedureQuestionEntry(task, column)?.item;

const formatRelationLabels = (value: any) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => item?.label ?? item?.fullName ?? item?.nombre ?? item)
      .filter(Boolean)
      .join(', ');
  }
  return value?.label ?? value?.fullName ?? value?.nombre ?? '';
};

const formatTime = (value: any) => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{2}:\d{2}$/.test(value.trim())) {
    return value.trim();
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('HH:mm') : '';
};

const isEmptyMultipleChoiceValue = (value: any) => value === null || value === undefined || value === '';

const alternativeMatchesMultipleChoice = (alt: any, value: any) => {
  if (isEmptyMultipleChoiceValue(value) || !alt) return false;
  const id = alt.id;
  const altValue = alt.value;
  const label = alt.label;
  if (!isEmptyMultipleChoiceValue(id) && id === value) return true;
  if (!isEmptyMultipleChoiceValue(altValue) && altValue === value) return true;
  if (!isEmptyMultipleChoiceValue(label) && label === value) return true;
  return false;
};

const formatMultipleChoice = (question: any, value: any) => {
  if (isEmptyMultipleChoiceValue(value)) return '';

  if (value === 'others') {
    return question?.multipleChoiceOthers
      ? `${i18n('entities.firma.parcial.other')}: ${question.multipleChoiceOthers}`
      : i18n('entities.firma.parcial.other');
  }

  const alternativas = question?.alternativas || [];
  const treeLabel = getMultipleChoiceTreeDisplayLabel(
    alternativas,
    value,
    question,
    'dynamicTable',
    findMultipleChoiceTreeFullPath,
  );
  if (treeLabel) return treeLabel;

  const match = alternativas.find((alt) => alternativeMatchesMultipleChoice(alt, value));
  return match?.label || value || '';
};

const formatMultiCheckbox = (question: any, value: any, tenantId?: string | null) => {
  const selected = (Array.isArray(value) ? value : [])
    .filter((item) => Boolean(item?.value))
    .map((item) => item?.pathLabel || item?.fullLabel || item?.label)
    .filter(Boolean);

  const questionLabel = question?.pregunta?.label || question?.pregunta?.titulo || question?.label || question?.titulo || '';
  const joiner = isBlumarPozosQuestionLabel(questionLabel, tenantId) ? '-' : ', ';
  let text = selected.join(joiner);
  const others = (Array.isArray(value) ? value : []).find((item) => item?.id === 'others');
  if (others?.value && question?.multiCheckboxOthers) {
    text = text ? `${text}: ${question.multiCheckboxOthers}` : question.multiCheckboxOthers;
  }
  return text;
};

const formatAttachmentsSummary = (values: any[], label: string) => {
  const count = Array.isArray(values) ? values.filter(Boolean).length : 0;
  return count > 0 ? `${count} ${label}` : '';
};

export type FormatProcedureAnswerOptions = {
  includeAgregados?: boolean;
  /** Si es false, omite `.comentario` de los agregados (p. ej. PDF tabulado lo muestra en el pie). */
  includeComentario?: boolean;
  /** Si es false, omite el resumen de cantidad de additionalPhotos (p. ej. PDF las muestra en sección aparte). */
  includeAdditionalPhotos?: boolean;
  /** True si una sección o subsección ancestro está marcada N/A. */
  ancestorNotApplicable?: boolean;
  tenantId?: string | null;
};

const getAgregadosSummary = (question: any, options: FormatProcedureAnswerOptions = {}) => {
  const {includeComentario = true, includeAdditionalPhotos = true} = options;
  const parts: string[] = [];
  if (question?.haveCriticality && question?.criticality) {
    parts.push(i18n(`entities.tarea.enumerators.prioridad.${question.criticality}`));
  }
  if (includeComentario && question?.tieneComentario && question?.comentario) {
    parts.push(String(question.comentario));
  }
  const observacion = question?.observacion || question?.observation;
  if (observacion && observacion !== question?.comentario) {
    parts.push(String(observacion));
  }
  if (includeAdditionalPhotos) {
    const photos = formatAttachmentsSummary(question?.additionalPhotos, i18n('entities.firma.parcial.images'));
    if (photos) parts.push(photos);
  }
  const files = formatAttachmentsSummary(question?.additionalFiles, i18n('entities.firma.parcial.files'));
  if (files) parts.push(files);
  return parts.join('\n');
};

export const isQuestionNotApplicable = (question: any) => Boolean(question?.allowNotApplicable && question?.notApplicable);

export const isContainerNotApplicable = (node: any) => Boolean(node?.allowNotApplicable && node?.notApplicable);

export function createAncestorNaTracker() {
  let sectionNa = false;
  let subsectionNa = false;
  return {
    observe(item: any) {
      if (!item) return;
      if (item.tipo === 'seccion' || item.tipo === 'header') {
        sectionNa = isContainerNotApplicable(item);
        subsectionNa = false;
        return;
      }
      if (item.tipo === 'subseccion') {
        subsectionNa = isContainerNotApplicable(item);
      }
    },
    get sectionNa() {
      return sectionNa;
    },
    get subsectionNa() {
      return subsectionNa;
    },
    get covered() {
      return sectionNa || subsectionNa;
    },
  };
}

export const formatProcedureAnswerText = (question: any, emptyText = '', options: FormatProcedureAnswerOptions = {}) => {
  if (!question?.tipoRespuesta) return emptyText;
  const {includeAgregados = true, ancestorNotApplicable = false} = options;
  const type = question.tipoRespuesta;
  const value = question[type];
  let text: any = '';

  if (ancestorNotApplicable || isQuestionNotApplicable(question)) {
    text = i18n('common.notApplicable');
  } else if (type === 'signature') {
    text =
      question?.signatureRef?.publicUrl || question?.signatureRef?.downloadUrl || question?.signature
        ? i18n('entities.firma.parcial.signatureLoaded')
        : '';
  } else if (type === 'date') {
    text = value ? moment(value).format('DD-MM-YYYY') : '';
  } else if (type === 'time') {
    text = formatTime(value);
  } else if (type === 'multipleChoice') {
    text = formatMultipleChoice(question, value);
  } else if (type === 'multiCheckbox') {
    text = formatMultiCheckbox(question, value, options.tenantId);
  } else if (['usuario', 'user', 'perfilEmpleado', 'thirdParty', 'asset', 'location', 'articulo', 'project'].includes(type)) {
    text = formatRelationLabels(value);
  } else if (type === 'files') {
    text =
      formatRelationLabels(value) ||
      formatAttachmentsSummary(question?.files || question?.images || value, i18n('entities.firma.parcial.files'));
  } else if (type === 'images' || type === 'textFromImage') {
    text = formatAttachmentsSummary(value || question?.images || question?.image, i18n('entities.firma.parcial.images'));
  } else if (Array.isArray(value)) {
    text = formatRelationLabels(value);
  } else if (value && typeof value === 'object') {
    text = formatRelationLabels(value);
  } else if (value === true) {
    text = i18n('common.yes');
  } else if (value === false) {
    text = i18n('common.no');
  } else {
    text = value ?? '';
  }

  const baseText = text != null ? String(text) : '';
  if (!includeAgregados) return baseText || emptyText;

  const agregados = getAgregadosSummary(question, options);
  const combined = [baseText, agregados].filter(Boolean).join('\n');
  return combined || emptyText;
};

const getProcedureQuestionLabel = (item: any) =>
  item?.pregunta?.label || item?.pregunta?.titulo || item?.label || item?.titulo || '';

const findProcedureQuestionByLabel = (task: any, label: string) =>
  (task?.listaProcedimiento || []).find((item) => {
    if (!(item?.tipo === 'pregunta' || item?.pregunta)) return false;
    return getProcedureQuestionLabel(item).trim().toUpperCase() === label;
  });

/** Prefiere "FECHA HORA" de la misma tarea; si no hay, usa el label de la pregunta con comentario. */
const getObservacionContextLabel = (task: any, questionItem: any) => {
  const fechaQuestion = findProcedureQuestionByLabel(task, 'FECHA');
  const horaQuestion = findProcedureQuestionByLabel(task, 'HORA');
  const fecha = fechaQuestion ? formatProcedureAnswerText(fechaQuestion, '', {includeAgregados: false}).trim() : '';
  const hora = horaQuestion ? formatProcedureAnswerText(horaQuestion, '', {includeAgregados: false}).trim() : '';
  const fechaHora = [fecha, hora].filter(Boolean).join(' ');
  if (fechaHora) return fechaHora;
  return getProcedureQuestionLabel(questionItem);
};

export type ProcedureComentarioEntry = {
  rowNumber: string | number;
  contextLabel: string;
  comentario: string;
};

/** Extrae comentarios de respuestas de procedimiento, con contexto de fila / fecha-hora o pregunta. */
export const collectProcedureComentarios = (
  tasks: any[] = [],
  options: {
    useSequentialRowNumbers?: boolean;
    /** Si true, el prefijo de observación es el N° de fila (omite fecha/hora). */
    useRowNumberContext?: boolean;
  } = {},
): ProcedureComentarioEntry[] => {
  const entries: ProcedureComentarioEntry[] = [];
  (tasks || []).forEach((task, index) => {
    const rowNumber = options.useSequentialRowNumbers ? index + 1 : getTaskDisplayNumber(task, index + 1);
    (task?.listaProcedimiento || [])
      .filter((item) => item?.tipo === 'pregunta' || item?.pregunta)
      .forEach((item) => {
        const comentario = item?.tieneComentario && item?.comentario ? String(item.comentario).trim() : '';
        if (!comentario) return;
        entries.push({
          rowNumber,
          contextLabel: options.useRowNumberContext ? String(rowNumber) : getObservacionContextLabel(task, item),
          comentario,
        });
      });
  });
  return entries;
};

export type ProcedureAdditionalPhotoEntry = {
  rowNumber: string | number;
  contextLabel: string;
  photo: any;
};

const getAttachmentUrl = (attachment: any) =>
  attachment?.publicUrl || attachment?.downloadUrl || (typeof attachment === 'string' ? attachment : null);

/** Extrae additionalPhotos de respuestas, con el mismo contexto de fila que las observaciones. */
export const collectProcedureAdditionalPhotos = (
  tasks: any[] = [],
  options: {
    useSequentialRowNumbers?: boolean;
    useRowNumberContext?: boolean;
  } = {},
): ProcedureAdditionalPhotoEntry[] => {
  const entries: ProcedureAdditionalPhotoEntry[] = [];
  (tasks || []).forEach((task, index) => {
    const rowNumber = options.useSequentialRowNumbers ? index + 1 : getTaskDisplayNumber(task, index + 1);
    (task?.listaProcedimiento || [])
      .filter((item) => item?.tipo === 'pregunta' || item?.pregunta)
      .forEach((item) => {
        const photos = (Array.isArray(item?.additionalPhotos) ? item.additionalPhotos : []).filter((photo) =>
          Boolean(getAttachmentUrl(photo)),
        );
        if (!photos.length) return;
        const contextLabel = options.useRowNumberContext ? String(rowNumber) : getObservacionContextLabel(task, item);
        photos.forEach((photo) => {
          entries.push({rowNumber, contextLabel, photo});
        });
      });
  });
  return entries;
};

const parseAlertBound = (value: any, fallback: number) => {
  if (value === null || value === undefined || value === '') return fallback;
  const numeric = Number(String(value).replace(',', '.'));
  return Number.isNaN(numeric) ? fallback : numeric;
};

/** Misma regla que `checkAlertRange` del formulario (web/app): `invertAlertNumberRange` alerta fuera de rango. */
export const isAnswerOutOfTolerance = (question: any) => {
  if (!question || isQuestionNotApplicable(question)) return false;
  if (question.tipoRespuesta !== 'numberField' || !question.hasAlertNumberRange) return false;
  const range = question.alertNumberRange;
  if (!Array.isArray(range) || range.length === 0) return false;
  const raw = question.numberField;
  if (raw === null || raw === undefined || raw === '') return false;
  const value = Number(String(raw).replace(',', '.'));
  if (Number.isNaN(value)) return false;
  const min = parseAlertBound(range[0], -Infinity);
  const max = parseAlertBound(range[1], Infinity);
  const inRange = value >= min && value <= max;
  return question.invertAlertNumberRange ? !inRange : inRange;
};

const normalizeAnswerLabel = (text: string) =>
  String(text || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

/** "No conforme" es una alternativa configurada manualmente en formularios de Plastyverg. */
export const isNoConformeAnswer = (question: any) => {
  if (!question || isQuestionNotApplicable(question)) return false;
  if (!['multipleChoice', 'multiCheckbox'].includes(question.tipoRespuesta)) return false;
  const text = normalizeAnswerLabel(formatProcedureAnswerText(question, '', {includeAgregados: false}));
  return /(^|[^a-z])no conforme([^a-z]|$)/.test(text);
};

export const isParcialAnswerAlert = (task: any, column: ParcialColumn, tenantId?: string | null) => {
  const entry = findProcedureQuestionEntry(task, column);
  if (!entry || entry.ancestorNotApplicable) return false;
  const question = entry.item;
  if (isAnswerOutOfTolerance(question)) return true;
  return isPlastyvergTenant(tenantId) && isNoConformeAnswer(question);
};

export const PARCIAL_ANSWER_ALERT_CELL_SX = {backgroundColor: '#f8d7da', color: '#842029', fontWeight: 600};

export const getParcialAnswerText = (
  task: any,
  column: ParcialColumn,
  emptyText = '',
  options: FormatProcedureAnswerOptions = {},
) => {
  const entry = findProcedureQuestionEntry(task, column);
  return formatProcedureAnswerText(entry?.item, emptyText, {
    ...options,
    ancestorNotApplicable: Boolean(entry?.ancestorNotApplicable),
  });
};

export const buildParcialGroups = (tasks: any[] = []) =>
  groupTasksByModelo(tasks)
    .map((group) => ({
      ...group,
      columns: buildParcialColumns(group.inspecciones),
    }))
    .sort((left, right) => right.columns.length - left.columns.length);

export const buildActiveParcialChildMap = (parciales: any[] = []) => {
  const map = new Map<string, any>();
  (parciales || [])
    .filter((parcial) => ['Pendiente', 'Aprobada'].includes(parcial?.estado))
    .forEach((parcial) => {
      (parcial?.tareasVinculadas || []).forEach((task) => {
        const id = getRecordId(task);
        if (id) map.set(id, parcial);
      });
    });
  return map;
};
